import { Star } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export type FavoritEintrag = { accountId: string | null; name: string };

type SternProps = {
  istFavorit: boolean;
  favoriten?: FavoritEintrag[];
  /** Ohne Handler ist der Stern nur Anzeige (fehlendes Recht). */
  onToggle?: (favorit: boolean) => void;
  className?: string;
};

/**
 * Der Favoriten-Stern mit den Namen derer, die vorgemerkt haben (T-0014).
 *
 * Vorbild ist die Anfragen-Karte; Probestunden hatten einen eigenen Kasten-Knopf
 * ohne Namen. Beide Listen nutzen jetzt diesen einen Baustein.
 */
export function FavoritStern({ istFavorit, favoriten = [], onToggle, className }: SternProps) {
  const namen = favoriten.map((eintrag) => eintrag.name).join(', ');
  const titel = namen ? `Vorgemerkt von ${namen}` : onToggle ? 'Für mich vormerken' : 'Nicht vorgemerkt';
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <button
        type="button"
        aria-pressed={istFavorit}
        aria-label={titel}
        title={titel}
        disabled={!onToggle}
        className={cn(
          'rounded-full p-1 transition-colors disabled:cursor-default',
          istFavorit ? 'text-amber-500' : 'text-muted-foreground hover:text-amber-500',
        )}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onToggle?.(!istFavorit);
        }}
      >
        <Star className="h-4 w-4" fill={istFavorit ? 'currentColor' : 'none'} />
      </button>
      {namen ? <span className="text-xs text-muted-foreground">{namen}</span> : null}
    </div>
  );
}

type FilterProps = {
  aktiv: boolean;
  onChange: (aktiv: boolean) => void;
  titel?: string;
};

/** „Meine Favoriten“ als Schalter-Knopf neben der Suche (T-0014). */
export function MeineFavoritenButton({ aktiv, onChange, titel }: FilterProps) {
  return (
    <Button
      type="button"
      variant={aktiv ? 'default' : 'outline'}
      aria-pressed={aktiv}
      onClick={() => onChange(!aktiv)}
      title={titel ?? 'Nur die Einträge zeigen, die ich mir vorgemerkt habe'}
    >
      <Star className="mr-2 h-4 w-4" fill={aktiv ? 'currentColor' : 'none'} />
      Meine Favoriten
    </Button>
  );
}
